import type { TopzleGameDefinition } from "@topzle/game-sdk";
import type { BotContext, GameResult, ViewerContext } from "@topzle/types";
import { z } from "zod";

export const BATTLESHIP_BOARD_SIZE = 10;
export const BATTLESHIP_CELL_COUNT = BATTLESHIP_BOARD_SIZE * BATTLESHIP_BOARD_SIZE;
export const BATTLESHIP_SHOT_SEQUENCE_MS = 3_500;
export const BATTLESHIP_SUNK_SEQUENCE_MS = 5_600;
export const BATTLESHIP_FAST_SHOT_SEQUENCE_MS = 1_650;
export const BATTLESHIP_FAST_SUNK_SEQUENCE_MS = 2_500;
export const battleshipFleet = [
  { id: "carrier", name: "Carrier", length: 5 },
  { id: "battleship", name: "Battleship", length: 4 },
  { id: "cruiser", name: "Cruiser", length: 3 },
  { id: "submarine", name: "Submarine", length: 3 },
  { id: "destroyer", name: "Destroyer", length: 2 },
] as const;

const shipIdSchema = z.enum(["carrier", "battleship", "cruiser", "submarine", "destroyer"]);
const shipSchema = z.object({
  id: shipIdSchema,
  cells: z
    .array(
      z
        .number()
        .int()
        .min(0)
        .max(BATTLESHIP_CELL_COUNT - 1),
    )
    .min(0)
    .max(5),
});
const shotSchema = z.object({
  index: z
    .number()
    .int()
    .min(0)
    .max(BATTLESHIP_CELL_COUNT - 1),
  result: z.enum(["hit", "miss"]),
  shipId: shipIdSchema.nullable(),
  sunkShipId: shipIdSchema.nullable(),
});
const boardSchema = z.object({
  playerId: z.string(),
  ships: z.array(shipSchema).length(battleshipFleet.length),
  shotsReceived: z.array(shotSchema).max(BATTLESHIP_CELL_COUNT),
});
const lastShotSchema = shotSchema.extend({
  attackerId: z.string(),
  defenderId: z.string(),
});
const orientationSchema = z.enum(["horizontal", "vertical"]);
const botInsightSchema = z.object({
  playerId: z.string(),
  targetIndex: z
    .number()
    .int()
    .min(0)
    .max(BATTLESHIP_CELL_COUNT - 1),
  strategy: z.enum(["recon", "hunt", "line", "density"]),
  reason: z.string().max(220),
  confidence: z.number().int().min(0).max(99),
  consideredPlacements: z.number().int().nonnegative(),
});
const battleshipBotPolicySchema = z.enum([
  "random",
  "hunt",
  "probability",
  "probability-defensive",
]);

export const battleshipStateSchema = z.object({
  players: z.array(z.string()).length(2),
  boards: z.array(boardSchema).length(2),
  readyIds: z.array(z.string()).max(2),
  turnIndex: z.number().int().min(0).max(1),
  phase: z.enum(["placement", "playing", "finished"]),
  winnerId: z.string().nullable(),
  lastShot: lastShotSchema.nullable(),
  round: z.number().int().positive(),
  scores: z.record(z.string(), z.number().int().nonnegative()),
  battleScores: z.record(z.string(), z.number().int().nonnegative()),
  lastBotInsight: botInsightSchema.nullable().default(null),
  actionAvailableAt: z.number().int().nonnegative().default(0),
  fastMode: z.boolean().default(false),
  botPolicy: battleshipBotPolicySchema.default("probability"),
});

export const battleshipEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("shuffle") }),
  z.object({
    type: z.literal("place_ship"),
    shipId: shipIdSchema,
    startIndex: z
      .number()
      .int()
      .min(0)
      .max(BATTLESHIP_CELL_COUNT - 1),
    orientation: orientationSchema,
  }),
  z.object({ type: z.literal("ready") }),
  z.object({
    type: z.literal("fire"),
    index: z
      .number()
      .int()
      .min(0)
      .max(BATTLESHIP_CELL_COUNT - 1),
    rationale: z.string().max(220).optional(),
    strategy: z.enum(["recon", "hunt", "line", "density"]).optional(),
    confidence: z.number().int().min(0).max(99).optional(),
    consideredPlacements: z.number().int().nonnegative().optional(),
  }),
  z.object({ type: z.literal("rematch") }),
  z.object({ type: z.literal("set_fast_mode"), enabled: z.boolean() }),
]);

export const battleshipSettingsSchema = z.object({
  randomFleet: z.boolean().default(true),
  botPolicy: battleshipBotPolicySchema.optional(),
});

export type BattleshipShipId = z.infer<typeof shipIdSchema>;
export type BattleshipOrientation = z.infer<typeof orientationSchema>;
export type BattleshipShot = z.infer<typeof shotSchema>;
export type BattleshipState = z.infer<typeof battleshipStateSchema>;
export type BattleshipEvent = z.infer<typeof battleshipEventSchema>;
export type BattleshipSettings = z.infer<typeof battleshipSettingsSchema>;
export type BattleshipBotInsight = z.infer<typeof botInsightSchema>;

export interface BattleshipPublicShip {
  id: BattleshipShipId;
  length: number;
  cells: number[];
  hits: number[];
  sunk: boolean;
}

export interface BattleshipPublicBoard {
  playerId: string;
  ships: BattleshipPublicShip[];
  shotsReceived: BattleshipShot[];
  fleetVisible: boolean;
}

export interface BattleshipPublicState {
  players: string[];
  boards: BattleshipPublicBoard[];
  readyIds: string[];
  turnIndex: number;
  phase: "placement" | "playing" | "finished";
  winnerId: string | null;
  lastShot: (BattleshipShot & { attackerId: string; defenderId: string }) | null;
  round: number;
  scores: Record<string, number>;
  battleScores: Record<string, number>;
  lastBotInsight: BattleshipBotInsight | null;
  actionAvailableAt: number;
  fastMode?: boolean;
  botPolicy?: z.infer<typeof battleshipBotPolicySchema>;
}

type RandomInt = (maximumExclusive: number) => number;

function randomChoiceIndex(length: number, randomInt?: RandomInt): number {
  if (length <= 1) return 0;
  const value = randomInt?.(length) ?? 0;
  return Math.abs(value) % length;
}

export function getBattleshipPlacementCells(
  startIndex: number,
  length: number,
  orientation: BattleshipOrientation,
): number[] | null {
  const row = Math.floor(startIndex / BATTLESHIP_BOARD_SIZE);
  const column = startIndex % BATTLESHIP_BOARD_SIZE;
  if (
    (orientation === "horizontal" && column + length > BATTLESHIP_BOARD_SIZE) ||
    (orientation === "vertical" && row + length > BATTLESHIP_BOARD_SIZE)
  ) {
    return null;
  }
  return Array.from({ length }, (_, offset) =>
    orientation === "horizontal"
      ? startIndex + offset
      : startIndex + offset * BATTLESHIP_BOARD_SIZE,
  );
}

function placementCandidates(length: number): number[][] {
  const candidates: number[][] = [];
  for (let startIndex = 0; startIndex < BATTLESHIP_CELL_COUNT; startIndex += 1) {
    for (const orientation of ["horizontal", "vertical"] as const) {
      const cells = getBattleshipPlacementCells(startIndex, length, orientation);
      if (cells) candidates.push(cells);
    }
  }
  return candidates;
}

export function isBattleshipPlacementLegal(
  ships: BattleshipState["boards"][number]["ships"],
  shipId: BattleshipShipId,
  cells: number[] | null,
): cells is number[] {
  if (!cells) return false;
  const shipDefinition = battleshipFleet.find((ship) => ship.id === shipId);
  if (!shipDefinition || cells.length !== shipDefinition.length) return false;
  const occupied = new Set(
    ships.filter((ship) => ship.id !== shipId).flatMap((ship) => ship.cells),
  );
  return cells.every((cell) => cell >= 0 && cell < BATTLESHIP_CELL_COUNT && !occupied.has(cell));
}

export function createBattleshipFleet(
  randomInt?: RandomInt,
): BattleshipState["boards"][number]["ships"] {
  const occupied = new Set<number>();
  return battleshipFleet.map((ship) => {
    const available = placementCandidates(ship.length).filter((cells) =>
      cells.every((cell) => !occupied.has(cell)),
    );
    const cells = available[randomChoiceIndex(available.length, randomInt)] ?? available[0] ?? [];
    for (const cell of cells) occupied.add(cell);
    return { id: ship.id, cells };
  });
}

function createEmptyBattleshipFleet(): BattleshipState["boards"][number]["ships"] {
  return battleshipFleet.map((ship) => ({ id: ship.id, cells: [] }));
}

export function isBattleshipFleetComplete(
  ships: BattleshipState["boards"][number]["ships"],
): boolean {
  const occupied = new Set<number>();
  for (const definition of battleshipFleet) {
    const ship = ships.find((candidate) => candidate.id === definition.id);
    if (!ship || ship.cells.length !== definition.length) return false;
    if (!isBattleshipPlacementLegal(ships, ship.id, ship.cells)) return false;
    for (const cell of ship.cells) {
      if (occupied.has(cell)) return false;
      occupied.add(cell);
    }
  }
  return occupied.size === 17;
}

export function createTacticalBattleshipFleet(
  randomInt?: RandomInt,
): BattleshipState["boards"][number]["ships"] {
  const occupied = new Set<number>();
  return battleshipFleet.map((ship) => {
    const available = placementCandidates(ship.length).filter((cells) =>
      cells.every((cell) => !occupied.has(cell)),
    );
    const ranked = available
      .map((cells) => {
        const edgeCells = cells.filter((cell) => {
          const row = Math.floor(cell / BATTLESHIP_BOARD_SIZE);
          const column = cell % BATTLESHIP_BOARD_SIZE;
          return row === 0 || row === 9 || column === 0 || column === 9;
        }).length;
        const nearby = cells.reduce(
          (total, cell) =>
            total +
            neighbors(cell).filter((neighbor) => occupied.has(neighbor) || cells.includes(neighbor))
              .length,
          0,
        );
        return {
          cells,
          score: edgeCells * 9 - nearby * 3 + randomChoiceIndex(7, randomInt),
        };
      })
      .sort((left, right) => right.score - left.score);
    const best = ranked.slice(0, Math.min(8, ranked.length));
    const cells =
      best[randomChoiceIndex(best.length, randomInt)]?.cells ??
      ranked[0]?.cells ??
      available[0] ??
      [];
    for (const cell of cells) occupied.add(cell);
    return { id: ship.id, cells };
  });
}

function shipIsSunk(
  ship: BattleshipState["boards"][number]["ships"][number],
  shots: BattleshipShot[],
): boolean {
  const definition = battleshipFleet.find((candidate) => candidate.id === ship.id);
  if (!definition || ship.cells.length !== definition.length) return false;
  const hits = new Set(shots.filter((shot) => shot.result === "hit").map((shot) => shot.index));
  return ship.cells.every((cell) => hits.has(cell));
}

function publicState(state: BattleshipState, viewer?: ViewerContext): BattleshipPublicState {
  return {
    ...state,
    players: [...state.players],
    readyIds: [...state.readyIds],
    scores: { ...state.scores },
    battleScores: { ...state.battleScores },
    lastShot: state.lastShot ? { ...state.lastShot } : null,
    boards: state.boards.map((board) => {
      const fleetVisible = state.phase === "finished" || board.playerId === viewer?.viewerId;
      return {
        playerId: board.playerId,
        fleetVisible,
        shotsReceived: board.shotsReceived.map((shot) => ({ ...shot })),
        ships: board.ships.map((ship) => {
          const sunk = shipIsSunk(ship, board.shotsReceived);
          return {
            id: ship.id,
            length: battleshipFleet.find((definition) => definition.id === ship.id)?.length ?? 0,
            cells: fleetVisible || sunk ? [...ship.cells] : [],
            hits: ship.cells.filter((cell) =>
              board.shotsReceived.some((shot) => shot.index === cell && shot.result === "hit"),
            ),
            sunk,
          };
        }),
      };
    }),
  };
}

function neighbors(index: number): number[] {
  const row = Math.floor(index / BATTLESHIP_BOARD_SIZE);
  const column = index % BATTLESHIP_BOARD_SIZE;
  return [
    row > 0 ? index - BATTLESHIP_BOARD_SIZE : -1,
    row < BATTLESHIP_BOARD_SIZE - 1 ? index + BATTLESHIP_BOARD_SIZE : -1,
    column > 0 ? index - 1 : -1,
    column < BATTLESHIP_BOARD_SIZE - 1 ? index + 1 : -1,
  ].filter((cell) => cell >= 0);
}

function battleTargetBoard(state: BattleshipState, playerId: string) {
  return state.boards.find((board) => board.playerId !== playerId);
}

function huntTargets(board: BattleshipState["boards"][number], shotIndexes: Set<number>): number[] {
  const candidates: number[] = [];
  for (const ship of board.ships) {
    if (shipIsSunk(ship, board.shotsReceived)) continue;
    const hits = board.shotsReceived
      .filter((shot) => shot.shipId === ship.id && shot.result === "hit")
      .map((shot) => shot.index);
    if (hits.length === 0) continue;
    const rows = new Set(hits.map((index) => Math.floor(index / BATTLESHIP_BOARD_SIZE)));
    const columns = new Set(hits.map((index) => index % BATTLESHIP_BOARD_SIZE));
    if (hits.length > 1 && rows.size === 1) {
      const ordered = [...hits].sort((left, right) => left - right);
      const first = ordered[0];
      const last = ordered.at(-1);
      if (first !== undefined && first % BATTLESHIP_BOARD_SIZE > 0) candidates.push(first - 1);
      if (last !== undefined && last % BATTLESHIP_BOARD_SIZE < 9) candidates.push(last + 1);
    } else if (hits.length > 1 && columns.size === 1) {
      const ordered = [...hits].sort((left, right) => left - right);
      const first = ordered[0];
      const last = ordered.at(-1);
      if (first !== undefined && first >= 10) candidates.push(first - 10);
      if (last !== undefined && last < 90) candidates.push(last + 10);
    } else {
      candidates.push(...hits.flatMap(neighbors));
    }
  }
  return [...new Set(candidates)].filter((index) => !shotIndexes.has(index));
}

interface RankedBattleshipTarget {
  index: number;
  score: number;
}

interface BattleshipProbabilityAnalysis {
  ranked: RankedBattleshipTarget[];
  consideredPlacements: number;
  largestRemaining: (typeof battleshipFleet)[number];
  latticeOffset: number;
}

function decisionEntropy(
  board: BattleshipState["boards"][number],
  playerId: string,
  entropy: number,
): number {
  let hash = Math.abs(Math.trunc(entropy));
  for (const character of playerId) hash = (hash * 31 + character.charCodeAt(0)) >>> 0;
  for (const shot of board.shotsReceived)
    hash =
      (hash * 131 +
        shot.index * 7 +
        (shot.result === "hit" ? 19 : 3) +
        (shot.sunkShipId ? 29 : 0)) >>>
      0;
  return hash;
}

function probabilityTargets(
  board: BattleshipState["boards"][number],
  shotIndexes: Set<number>,
  playerId: string,
  entropy: number,
): BattleshipProbabilityAnalysis {
  const scores = Array(BATTLESHIP_CELL_COUNT).fill(0) as number[];
  const misses = new Set(
    board.shotsReceived.filter((shot) => shot.result === "miss").map((shot) => shot.index),
  );
  const sunkShipIds = new Set(
    board.shotsReceived.flatMap((shot) => (shot.sunkShipId ? [shot.sunkShipId] : [])),
  );
  const sunkCells = new Set(
    board.shotsReceived
      .filter((shot) => shot.shipId && sunkShipIds.has(shot.shipId))
      .map((shot) => shot.index),
  );
  const unresolvedHits = board.shotsReceived.filter(
    (shot) => shot.result === "hit" && !sunkCells.has(shot.index),
  );
  const remainingShips = battleshipFleet.filter((ship) => !sunkShipIds.has(ship.id));
  const largestRemaining = remainingShips[0] ?? battleshipFleet.at(-1);
  if (!largestRemaining) throw new Error("No remaining fleet definition");
  const entropyValue = decisionEntropy(board, playerId, entropy);
  const latticeOffset = entropyValue % largestRemaining.length;
  const pursuit = new Set(huntTargets(board, shotIndexes));
  let consideredPlacements = 0;
  for (const definition of remainingShips) {
    const knownHits = new Set(
      board.shotsReceived
        .filter(
          (shot) =>
            shot.shipId === definition.id &&
            shot.result === "hit" &&
            !sunkShipIds.has(definition.id),
        )
        .map((shot) => shot.index),
    );
    for (const cells of placementCandidates(definition.length)) {
      if (cells.some((cell) => misses.has(cell) || sunkCells.has(cell))) continue;
      if ([...knownHits].some((hit) => !cells.includes(hit))) continue;
      if (unresolvedHits.some((hit) => hit.shipId !== definition.id && cells.includes(hit.index)))
        continue;
      consideredPlacements += 1;
      const largestShipWeight = definition.id === largestRemaining.id ? 3 : 1;
      const placementWeight = definition.length * definition.length * largestShipWeight;
      for (const cell of cells) {
        if (!shotIndexes.has(cell)) {
          const row = Math.floor(cell / BATTLESHIP_BOARD_SIZE);
          const column = cell % BATTLESHIP_BOARD_SIZE;
          const onLargestShipLattice = (row + column) % largestRemaining.length === latticeOffset;
          scores[cell] =
            (scores[cell] ?? 0) +
            placementWeight +
            (pursuit.has(cell) ? definition.length * 180 : 0) +
            (onLargestShipLattice && pursuit.size === 0 ? definition.length * 48 : 0);
        }
      }
    }
  }
  const available = scores.flatMap((score, index) =>
    !shotIndexes.has(index) ? [{ index, score }] : [],
  );
  available.sort(
    (left, right) =>
      right.score - left.score ||
      Number(right.index % 2 === Math.floor(right.index / BATTLESHIP_BOARD_SIZE) % 2) -
        Number(left.index % 2 === Math.floor(left.index / BATTLESHIP_BOARD_SIZE) % 2) ||
      left.index - right.index,
  );
  return {
    ranked: available,
    consideredPlacements,
    largestRemaining,
    latticeOffset,
  };
}

export interface BattleshipBotDecision {
  index: number;
  strategy: "recon" | "hunt" | "line" | "density";
  reason: string;
  confidence: number;
  consideredPlacements: number;
}

function targetCoordinate(index: number): string {
  return `${String.fromCharCode(65 + (index % BATTLESHIP_BOARD_SIZE))}${
    Math.floor(index / BATTLESHIP_BOARD_SIZE) + 1
  }`;
}

export function chooseBattleshipBotDecision(
  state: BattleshipState,
  playerId: string,
  difficulty: BotContext["difficulty"],
  entropy = 0,
): BattleshipBotDecision {
  const board = battleTargetBoard(state, playerId);
  if (!board) throw new Error("No target board");
  const shotIndexes = new Set(board.shotsReceived.map((shot) => shot.index));
  const available = Array.from({ length: BATTLESHIP_CELL_COUNT }, (_, index) => index).filter(
    (index) => !shotIndexes.has(index),
  );
  if (available.length === 0) throw new Error("No legal targets");
  if (difficulty === "easy") {
    const index =
      available[(board.shotsReceived.length * 17 + 7) % available.length] ?? available[0] ?? 0;
    return {
      index,
      strategy: "recon",
      reason: `Recon shot at ${targetCoordinate(index)} from the remaining untested waters.`,
      confidence: 25,
      consideredPlacements: 0,
    };
  }
  const targets = huntTargets(board, shotIndexes);
  const analysis = probabilityTargets(board, shotIndexes, playerId, entropy);
  const topScore = analysis.ranked[0]?.score ?? 0;
  const topBand = analysis.ranked.filter(
    (candidate, index) => index < 5 && candidate.score >= topScore * 0.97,
  );
  const entropyValue = decisionEntropy(board, playerId, entropy);
  if (difficulty === "hard") {
    const best = topBand[entropyValue % Math.max(1, topBand.length)] ??
      analysis.ranked[0] ?? { index: available[0] ?? 0, score: 0 };
    const secondScore =
      analysis.ranked.find((candidate) => candidate.index !== best.index)?.score ?? 0;
    const alignedHitCount = board.shotsReceived.filter(
      (shot) => shot.result === "hit" && !shot.sunkShipId,
    ).length;
    const strategy = targets.includes(best.index)
      ? alignedHitCount > 1
        ? "line"
        : "hunt"
      : "density";
    return {
      index: best.index,
      strategy,
      reason:
        strategy === "line"
          ? `Extending the confirmed hit line toward ${targetCoordinate(best.index)}.`
          : strategy === "hunt"
            ? `Pressing beside an unresolved hit at ${targetCoordinate(best.index)}.`
            : `${analysis.largestRemaining.name}-first probability sweep: ${targetCoordinate(
                best.index,
              )} covers the strongest legal placements on a ${analysis.largestRemaining.length}-cell diagonal lattice.`,
      confidence: Math.min(
        99,
        Math.max(38, Math.round((best.score / Math.max(1, best.score + secondScore)) * 100)),
      ),
      consideredPlacements: analysis.consideredPlacements,
    };
  }
  if (targets.length > 0) {
    const rankedTargets = analysis.ranked.filter((candidate) => targets.includes(candidate.index));
    const targetBand = rankedTargets.filter(
      (candidate, index) =>
        index < 3 && candidate.score >= (rankedTargets[0]?.score ?? candidate.score) * 0.9,
    );
    const index =
      targetBand[entropyValue % Math.max(1, targetBand.length)]?.index ??
      rankedTargets[0]?.index ??
      targets[0] ??
      available[0] ??
      0;
    return {
      index,
      strategy: "hunt",
      reason: `Following the unresolved contact toward ${targetCoordinate(index)}.`,
      confidence: 68,
      consideredPlacements: analysis.consideredPlacements,
    };
  }
  const mediumBand = analysis.ranked.filter(
    (candidate, index) => index < 8 && candidate.score >= topScore * 0.88,
  );
  const index =
    mediumBand[entropyValue % Math.max(1, mediumBand.length)]?.index ??
    analysis.ranked[0]?.index ??
    available[0] ??
    0;
  return {
    index,
    strategy: "density",
    reason: `${analysis.largestRemaining.name}-first sweep at ${targetCoordinate(
      index,
    )}, spacing probes every ${analysis.largestRemaining.length} diagonals while preserving some variation.`,
    confidence: 54,
    consideredPlacements: analysis.consideredPlacements,
  };
}

export function chooseBattleshipBotTarget(
  state: BattleshipState,
  playerId: string,
  difficulty: BotContext["difficulty"],
): number {
  return chooseBattleshipBotDecision(state, playerId, difficulty).index;
}

function freshBoards(
  players: string[],
  randomInt?: RandomInt,
  botPolicy: z.infer<typeof battleshipBotPolicySchema> = "probability",
): BattleshipState["boards"] {
  return players.map((playerId) => ({
    playerId,
    ships: playerId.startsWith("ai:")
      ? botPolicy === "probability-defensive"
        ? createTacticalBattleshipFleet(randomInt)
        : createBattleshipFleet(randomInt)
      : createEmptyBattleshipFleet(),
    shotsReceived: [],
  }));
}

export const battleshipGame: TopzleGameDefinition<
  BattleshipState,
  BattleshipEvent,
  BattleshipSettings,
  { ships: BattleshipState["boards"][number]["ships"] }
> = {
  id: "battleship",
  version: "1.2.0",
  name: "Battleship",
  shortDescription: "Hide your fleet, read the grid, and sink every enemy ship.",
  longDescription:
    "A private-information naval duel with manual fleet deployment, server-hidden ships, probability-driven tactical bots, and spectators.",
  category: "board",
  icon: "ship-wheel",
  cover: "battleship",
  minPlayers: 2,
  maxPlayers: 2,
  supportsGuests: true,
  supportsLocalPlay: true,
  supportsOnlinePlay: true,
  supportsSpectators: true,
  supportsTeams: false,
  supportsAiPlayers: true,
  supportsLocalAi: true,
  supportsCloudAi: false,
  supportsTournaments: true,
  supportsPersistence: true,
  requiredCapabilities: ["text"],
  supportedLayouts: ["bubbles", "rail", "game_fullscreen", "split", "avatar_only"],
  preferredLayout: "game_fullscreen",
  stateSchema: battleshipStateSchema,
  eventSchema: battleshipEventSchema,
  settingsSchema: battleshipSettingsSchema,
  createInitialState(context, settings) {
    const players = [context.playerIds[0] ?? "player-one", context.playerIds[1] ?? "player-two"];
    return {
      players,
      boards: freshBoards(players, context.randomInt, settings.botPolicy ?? "probability"),
      readyIds: [],
      turnIndex: 0,
      phase: "placement",
      winnerId: null,
      lastShot: null,
      round: 1,
      scores: Object.fromEntries(players.map((playerId) => [playerId, 0])),
      battleScores: Object.fromEntries(players.map((playerId) => [playerId, 0])),
      lastBotInsight: null,
      actionAvailableAt: 0,
      fastMode: false,
      botPolicy: settings.botPolicy ?? "probability",
    };
  },
  validateEvent(state, event, context) {
    const actorId = context.actorId ?? "";
    if (!state.players.includes(actorId))
      return { valid: false, code: "spectator", message: "Spectators cannot command a fleet" };
    if (event.type === "set_fast_mode")
      return {
        valid: actorId === state.players[0],
        code: "host_only",
        message: "Only the first player can change Fast mode",
      };
    if (event.type === "rematch")
      return {
        valid: state.phase === "finished",
        code: "battle_active",
        message: "Finish this battle first",
      };
    if (event.type === "shuffle" || event.type === "place_ship" || event.type === "ready") {
      if (state.phase !== "placement")
        return { valid: false, code: "battle_started", message: "The battle has already started" };
      if (state.readyIds.includes(actorId))
        return { valid: false, code: "fleet_locked", message: "Your fleet is already locked" };
      if (event.type === "place_ship") {
        const board = state.boards.find((candidate) => candidate.playerId === actorId);
        const definition = battleshipFleet.find((ship) => ship.id === event.shipId);
        const cells = definition
          ? getBattleshipPlacementCells(event.startIndex, definition.length, event.orientation)
          : null;
        if (!board || !isBattleshipPlacementLegal(board.ships, event.shipId, cells))
          return {
            valid: false,
            code: "illegal_placement",
            message: "That ship would overlap another ship or leave the battle grid",
          };
      }
      if (event.type === "ready") {
        const board = state.boards.find((candidate) => candidate.playerId === actorId);
        if (!board || !isBattleshipFleetComplete(board.ships))
          return {
            valid: false,
            code: "fleet_incomplete",
            message: "Place all five ships before locking your fleet",
          };
      }
      return { valid: true };
    }
    if (state.phase !== "playing")
      return { valid: false, code: "not_firing", message: "Lock both fleets before firing" };
    if (state.players[state.turnIndex] !== actorId)
      return { valid: false, code: "not_your_turn", message: "Wait for your turn" };
    if (context.now < state.actionAvailableAt)
      return {
        valid: false,
        code: "strike_resolving",
        message: "Wait for the current strike animation to finish",
      };
    const defender = battleTargetBoard(state, actorId);
    if (defender?.shotsReceived.some((shot) => shot.index === event.index))
      return { valid: false, code: "already_targeted", message: "You already fired there" };
    return { valid: Boolean(defender), code: "no_target", message: "No enemy fleet was found" };
  },
  reduceEvent(state, event, context) {
    const actorId = context.actorId ?? "";
    if (event.type === "set_fast_mode") return { ...state, fastMode: event.enabled };
    if (event.type === "rematch")
      return {
        ...state,
        boards: freshBoards(state.players, context.randomInt, state.botPolicy),
        readyIds: [],
        turnIndex: state.round % 2,
        phase: "placement",
        winnerId: null,
        lastShot: null,
        lastBotInsight: null,
        actionAvailableAt: 0,
        round: state.round + 1,
        battleScores: Object.fromEntries(state.players.map((playerId) => [playerId, 0])),
      };
    if (event.type === "shuffle")
      return {
        ...state,
        boards: state.boards.map((board) =>
          board.playerId === actorId
            ? { ...board, ships: createBattleshipFleet(context.randomInt) }
            : board,
        ),
      };
    if (event.type === "place_ship") {
      const definition = battleshipFleet.find((ship) => ship.id === event.shipId);
      const cells = definition
        ? getBattleshipPlacementCells(event.startIndex, definition.length, event.orientation)
        : null;
      if (!cells) return state;
      return {
        ...state,
        boards: state.boards.map((board) =>
          board.playerId === actorId
            ? {
                ...board,
                ships: board.ships.map((ship) =>
                  ship.id === event.shipId ? { ...ship, cells } : ship,
                ),
              }
            : board,
        ),
      };
    }
    if (event.type === "ready") {
      const board = state.boards.find((candidate) => candidate.playerId === actorId);
      if (!board || !isBattleshipFleetComplete(board.ships)) return state;
      const readyIds = [...new Set([...state.readyIds, actorId])];
      return {
        ...state,
        readyIds,
        phase: readyIds.length === 2 ? "playing" : "placement",
      };
    }
    const defenderIndex = state.boards.findIndex((board) => board.playerId !== actorId);
    const defender = state.boards[defenderIndex];
    if (!defender) return state;
    const ship = defender.ships.find((candidate) => candidate.cells.includes(event.index));
    const baseShot: BattleshipShot = {
      index: event.index,
      result: ship ? "hit" : "miss",
      shipId: ship?.id ?? null,
      sunkShipId: null,
    };
    const shotsWithHit = [...defender.shotsReceived, baseShot];
    const sunkShipId = ship && shipIsSunk(ship, shotsWithHit) ? ship.id : null;
    const shot = { ...baseShot, sunkShipId };
    const shotsReceived = [...defender.shotsReceived, shot];
    const boards = state.boards.map((board, index) =>
      index === defenderIndex ? { ...board, shotsReceived } : board,
    );
    const allSunk = defender.ships.every((candidate) => shipIsSunk(candidate, shotsReceived));
    const scores = { ...state.scores };
    if (allSunk) scores[actorId] = (scores[actorId] ?? 0) + 1;
    const battleScores = { ...state.battleScores };
    const sunkBonus = sunkShipId ? (ship?.cells.length ?? 0) * 50 : 0;
    battleScores[actorId] =
      (battleScores[actorId] ?? 0) + (ship ? 100 : 0) + sunkBonus + (allSunk ? 500 : 0);
    return {
      ...state,
      boards,
      phase: allSunk ? "finished" : "playing",
      winnerId: allSunk ? actorId : null,
      turnIndex: allSunk ? state.turnIndex : state.turnIndex === 0 ? 1 : 0,
      lastShot: {
        ...shot,
        attackerId: actorId,
        defenderId: defender.playerId,
      },
      lastBotInsight:
        actorId.startsWith("ai:") && event.rationale
          ? {
              playerId: actorId,
              targetIndex: event.index,
              strategy: event.strategy ?? "recon",
              reason: event.rationale,
              confidence: event.confidence ?? 0,
              consideredPlacements: event.consideredPlacements ?? 0,
            }
          : state.lastBotInsight,
      actionAvailableAt:
        context.now +
        (state.fastMode
          ? sunkShipId
            ? BATTLESHIP_FAST_SUNK_SEQUENCE_MS
            : BATTLESHIP_FAST_SHOT_SEQUENCE_MS
          : sunkShipId
            ? BATTLESHIP_SUNK_SEQUENCE_MS
            : BATTLESHIP_SHOT_SEQUENCE_MS),
      scores,
      battleScores,
    };
  },
  getPublicState: (state, viewer) => publicState(state, viewer),
  getPrivatePlayerState(state, playerId) {
    return {
      ships: state.boards.find((board) => board.playerId === playerId)?.ships ?? [],
    };
  },
  getSpectatorState: (state) => publicState(state),
  getAvailableActions(state, playerId) {
    if (!state.players.includes(playerId)) return [];
    if (state.phase === "placement")
      return state.readyIds.includes(playerId) ? [] : [{ type: "ready" }, { type: "shuffle" }];
    if (state.phase !== "playing" || state.players[state.turnIndex] !== playerId) return [];
    const defender = battleTargetBoard(state, playerId);
    const fired = new Set(defender?.shotsReceived.map((shot) => shot.index) ?? []);
    return Array.from({ length: BATTLESHIP_CELL_COUNT }, (_, index) => index)
      .filter((index) => !fired.has(index))
      .map((index) => ({ type: "fire", index }));
  },
  calculateResult(state): GameResult | null {
    if (state.phase !== "finished" || !state.winnerId) return null;
    return {
      status: "completed",
      winnerIds: [state.winnerId],
      scores: state.scores,
      reason: "fleet_destroyed",
    };
  },
  async createBotAction(state, botContext) {
    if (state.phase === "placement") return { type: "ready" };
    const policyDifficulty: BotContext["difficulty"] =
      botContext.policy === "random"
        ? "easy"
        : botContext.policy === "hunt"
          ? "medium"
          : botContext.policy === "probability" || botContext.policy === "probability-defensive"
            ? "hard"
            : botContext.difficulty;
    const decision = chooseBattleshipBotDecision(
      state,
      botContext.playerId,
      policyDifficulty,
      botContext.now,
    );
    return {
      type: "fire",
      index: decision.index,
      rationale: decision.reason,
      strategy: decision.strategy,
      confidence: decision.confidence,
      consideredPlacements: decision.consideredPlacements,
    };
  },
  handlePlayerDeparture(state, departure) {
    if (!state.players.includes(departure.playerId) || !departure.replacementPlayerId) return null;
    const replacementPlayerId = departure.replacementPlayerId;
    const replace = (id: string) => (id === departure.playerId ? replacementPlayerId : id);
    const scores = { ...state.scores };
    scores[replacementPlayerId] = scores[departure.playerId] ?? 0;
    delete scores[departure.playerId];
    const battleScores = { ...state.battleScores };
    battleScores[replacementPlayerId] = battleScores[departure.playerId] ?? 0;
    delete battleScores[departure.playerId];
    return {
      state: {
        ...state,
        players: state.players.map(replace),
        boards: state.boards.map((board) => {
          const playerId = replace(board.playerId);
          return {
            ...board,
            playerId,
            ships:
              playerId.startsWith("ai:") && !isBattleshipFleetComplete(board.ships)
                ? createTacticalBattleshipFleet()
                : board.ships,
          };
        }),
        readyIds: state.readyIds.map(replace),
        winnerId: state.winnerId ? replace(state.winnerId) : null,
        lastShot: state.lastShot
          ? {
              ...state.lastShot,
              attackerId: replace(state.lastShot.attackerId),
              defenderId: replace(state.lastShot.defenderId),
            }
          : null,
        scores,
        battleScores,
      },
      replacementPlayerId,
    };
  },
};
